import { Injectable } from '@angular/core';
import {
    browserSupportsWebAuthn,
    browserSupportsWebAuthnAutofill,
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
    startAuthentication,
    startRegistration,
    WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { firstValueFrom, Observable } from 'rxjs';
import {
    WebauthnControllerAdminFindByUser200ResponseDataInner,
    WebauthnControllerLoginVerify200Response,
    WebauthnControllerService,
} from 'projects/api-swagger-client/src';

import { DeviceService } from '../@shared/device.service';
import { RuntimeConfigService } from '../@shared/runtime-config.service';
import { CredentialsService } from './credentials.service';

export type Passkey = WebauthnControllerAdminFindByUser200ResponseDataInner;

/** Paso en que falló: pedir opciones, la ceremonia del navegador o la verificación. */
export type PasskeyPhase = 'options' | 'ceremony' | 'verify';

/** Error listo para mostrarse. `cancelled` = el usuario cerró el diálogo del navegador. */
export class PasskeyError extends Error {
    constructor(
        message: string,
        readonly phase: PasskeyPhase,
        readonly cancelled = false,
        /** Llave de error del backend (p. ej. `PasskeyInvalid`), si la hubo. */
        readonly code?: string
    ) {
        super(message);
    }
}

/**
 * Fase 7 — ceremonias de llaves de acceso (WebAuthn) contra el backend. El navegador
 * crea y firma con `@simplewebauthn/browser`; el backend genera las opciones y
 * verifica. La sesión la emite `AuthenticationService.loginWithPasskey`.
 */
@Injectable({
    providedIn: 'root',
})
export class PasskeyService {
    constructor(
        private webauthnControllerService: WebauthnControllerService,
        private deviceService: DeviceService,
        private runtimeConfigService: RuntimeConfigService,
        private credentialsService: CredentialsService
    ) {}

    isSupported(): boolean {
        return browserSupportsWebAuthn();
    }

    supportsAutofill(): Promise<boolean> {
        return browserSupportsWebAuthnAutofill();
    }

    /** La política deja usar llaves de acceso al usuario con sesión (claim del token). */
    isAllowedForCurrentUser(): boolean {
        return this.credentialsService.getDecodedToken()?.['passkeysAllowed'] === true;
    }

    /** Corta una ceremonia pendiente (p. ej. el autocompletado al salir del login). */
    cancel(): void {
        WebAuthnAbortService.cancelCeremony();
    }

    /**
     * Login sin contraseña. Con `useBrowserAutofill` el navegador ofrece la llave en
     * el autocompletado del campo de correo en vez de abrir un diálogo.
     */
    async authenticate(useBrowserAutofill = false): Promise<WebauthnControllerLoginVerify200Response> {
        const optionsResult = await this.call(
            () => this.webauthnControllerService.webauthnControllerLoginOptions({ platform: 'web' }),
            'options'
        );
        const { challengeId, options } = optionsResult.data ?? {};

        const response = await this.runCeremony(() =>
            startAuthentication({
                optionsJSON: options as PublicKeyCredentialRequestOptionsJSON,
                useBrowserAutofill,
            })
        );

        return this.call(
            () =>
                this.webauthnControllerService.webauthnControllerLoginVerify({
                    client_id: this.runtimeConfigService.clientId,
                    challengeId: challengeId!,
                    response: response as unknown as { [key: string]: unknown },
                    ...this.deviceService.getDeviceInfo(),
                }),
            'verify'
        );
    }

    /** Crea una llave de acceso para el usuario con sesión iniciada. */
    async register(): Promise<void> {
        const optionsResult = await this.call(
            () => this.webauthnControllerService.webauthnControllerRegisterOptions({ platform: 'web' }),
            'options'
        );
        const { challengeId, options } = optionsResult.data ?? {};

        const response = await this.runCeremony(() =>
            startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON })
        );

        await this.call(() =>
            this.webauthnControllerService.webauthnControllerRegisterVerify({
                challengeId: challengeId!,
                response: response as unknown as { [key: string]: unknown },
                name: this.deviceService.getDeviceInfo().deviceName,
            })
        );
    }

    async list(): Promise<Passkey[]> {
        const result = await this.call(() => this.webauthnControllerService.webauthnControllerFindMine());
        return result.data ?? [];
    }

    async rename(id: string, name: string): Promise<void> {
        await this.call(() => this.webauthnControllerService.webauthnControllerRename(id, { name }));
    }

    async remove(id: string): Promise<void> {
        await this.call(() => this.webauthnControllerService.webauthnControllerRemove(id));
    }

    async adminList(userId: number): Promise<Passkey[]> {
        const result = await this.call(() =>
            this.webauthnControllerService.webauthnControllerAdminFindByUser(userId)
        );
        return result.data ?? [];
    }

    async adminRemoveAll(userId: number): Promise<number> {
        const result = await this.call(() =>
            this.webauthnControllerService.webauthnControllerAdminRemoveAll(userId)
        );
        return result.data?.deleted ?? 0;
    }

    private async call<T>(request: () => Observable<T>, phase: PasskeyPhase = 'verify'): Promise<T> {
        try {
            return await firstValueFrom(request());
        } catch (error) {
            throw new PasskeyError(this.messageFor(error), phase, false, this.errorKey(error));
        }
    }

    private async runCeremony<T>(ceremony: () => Promise<T>): Promise<T> {
        try {
            return await ceremony();
        } catch (error) {
            const name = (error as { name?: string })?.name;
            // NotAllowedError = el usuario canceló o dejó vencer el diálogo;
            // AbortError = otra ceremonia (o el cambio de pantalla) cortó esta.
            if (name === 'NotAllowedError' || name === 'AbortError') {
                throw new PasskeyError('Operación cancelada', 'ceremony', true);
            }
            if (name === 'InvalidStateError') {
                throw new PasskeyError('Este dispositivo ya tiene una llave de acceso para tu cuenta.', 'ceremony');
            }
            if (name === 'SecurityError') {
                throw new PasskeyError('Las llaves de acceso no están disponibles en este sitio.', 'ceremony');
            }
            throw new PasskeyError('No se pudo completar la operación con la llave de acceso.', 'ceremony');
        }
    }

    private errorKey(error: unknown): string {
        const httpError = error as { error?: { message?: string; error?: { message?: string } } };
        return httpError?.error?.error?.message ?? httpError?.error?.message ?? '';
    }

    /** Escapa texto que va dentro del HTML de un diálogo de confirmación. */
    static escapeHtml(value: string): string {
        return value.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    }

    /** Traduce las llaves de error del backend (ver `webauthn.controller.ts`). */
    private messageFor(error: unknown): string {
        const httpError = error as { status?: number; error?: { message?: string } };
        if (httpError?.status === 429) {
            return httpError.error?.message ?? 'Demasiados intentos. Espera unos minutos.';
        }
        switch (this.errorKey(error)) {
            case 'PasskeysNotEnabled':
                return 'Las llaves de acceso aún no están habilitadas para tu cuenta.';
            case 'PasskeyReauthRequired':
                return 'Por seguridad, vuelve a iniciar sesión para crear una llave de acceso.';
            case 'PasskeyNotAllowedForCeo':
                return 'Tu cuenta entra con contraseña y selección de empresa; las llaves de acceso aún no están disponibles para ella.';
            case 'PasskeyChallengeExpired':
            case 'PasskeyChallengeInvalid':
                return 'La solicitud venció. Intenta de nuevo.';
            case 'PasskeyLimitReached':
                return 'Ya tienes 5 llaves de acceso, el máximo. Elimina una que no uses para crear otra.';
            case 'PasskeyAlreadyRegistered':
                return 'Esta llave de acceso ya estaba registrada.';
            case 'PasskeyInvalid':
                return 'No reconocimos esta llave de acceso. Entra con tu contraseña.';
            case 'PasskeyRegistrationInvalid':
                return 'No se pudo crear la llave de acceso. Intenta de nuevo.';
            case 'PasskeysNotConfigured':
                return 'Las llaves de acceso no están disponibles por ahora.';
        }
        if (httpError?.status === 0) {
            return 'No hay conexión con el servidor.';
        }
        return 'No se pudo completar la operación con la llave de acceso.';
    }
}
